import Bowser from "bowser";

import { SUPPORTED_BROWSERS } from "../config/supportedBrowsers";

export type SupportedPlatform = "mobile" | "desktop";

/** Which OS keys in the matrix belong to each platform category. */
const PLATFORM_OS: Record<SupportedPlatform, readonly string[]> = {
  mobile: ["ios", "android"],
  desktop: ["windows", "macos"],
};

/** Display names for the matrix's browser keys (which are bowser aliases). */
const BROWSER_DISPLAY_NAMES: Record<string, string> = {
  chrome: "Chrome",
  safari: "Safari",
  firefox: "Firefox",
  edge: "Edge",
  samsung_internet: "Samsung Internet",
};

/**
 * Returns `true` only when the visitor's browser is positively identified AND
 * its version is below the supported minimum for its OS (see EPP-3426).
 *
 * `satisfies()` returns:
 *   - `true`      -> browser is at/above the supported minimum (supported)
 *   - `false`     -> browser is below the supported minimum (unsupported)
 *   - `undefined` -> browser/OS not described in the matrix (unknown)
 *
 * Unknown browsers must NOT be warned, so we treat only an explicit `false` as
 * unsupported.
 */
export const isBrowserUnsupported = (userAgent?: string | null): boolean => {
  if (!userAgent) return false;

  try {
    return Bowser.getParser(userAgent).satisfies(SUPPORTED_BROWSERS) === false;
  } catch {
    // A malformed user agent should never block the user with a warning.
    return false;
  }
};

/** Coarse platform category, treating tablets as mobile. Defaults to desktop. */
export const getPlatform = (userAgent?: string | null): SupportedPlatform => {
  if (!userAgent) return "desktop";

  try {
    const type = Bowser.getParser(userAgent).getPlatformType(true);
    if (type === "tablet") return "mobile";
    return type;
  } catch {
    return "desktop";
  }
};

/**
 * Human-readable list of supported browsers for a platform, derived from the
 * matrix so it stays in sync when `SUPPORTED_BROWSERS` is edited.
 */
export const supportedBrowsersForPlatform = (platform: SupportedPlatform): string[] => {
  const names: string[] = [];

  for (const os of PLATFORM_OS[platform]) {
    const rules = SUPPORTED_BROWSERS[os];
    if (!rules) continue;

    for (const key of Object.keys(rules)) {
      const label = BROWSER_DISPLAY_NAMES[key] ?? key;
      if (!names.includes(label)) names.push(label);
    }
  }

  return names;
};
